import React, { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, Confirm, Form, Label, Message, Modal, TextArea } from 'semantic-ui-react'
import { ChatsCircle, CheckCircle } from 'phosphor-react'
import { QuestionThreadService } from 'utility/services/question-threads'
import { useNotification } from 'hooks/toast'
import {
    MAX_REVIEW_NOTE,
    buildReviewDonePayload,
    openCommentCount,
    openCommentsWarning,
    reviewStatusText,
    threadErrorMessage,
    validateReviewNote,
} from 'utility/question-threads'
import { useQuestionThreads } from './QuestionThreadsContext'

/**
 * "Review done" step (templates with ReviewDoneRequired): status, "Mark review done" (optional note) and "Undo",
 * following the server's CanMarkDone / CanUndo.
 */
const ReviewDoneControl = () => {
    const ctx = useQuestionThreads()
    const { successToast, errorToast } = useNotification()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [note, setNote] = useState('')
    const [busy, setBusy] = useState(false)
    const [confirmUndo, setConfirmUndo] = useState(false)
    const mountedRef = useRef(true)
    useEffect(
        () => () => {
            mountedRef.current = false
        },
        [],
    )
    const { review } = ctx
    if (!review || !review.Required) return null
    const stillOpen = openCommentCount(ctx.counts)

    const run = (call, okText) => {
        setBusy(true)
        return call()
            .then(response => {
                if (response && response.Success === false) {
                    errorToast(threadErrorMessage({ error: response }))
                    return false
                }
                if (response && response.Review) ctx.applyReview(response.Review)
                else ctx.reload()
                successToast(okText)
                return true
            })
            .catch(error => {
                const message = threadErrorMessage(error)
                if (message) errorToast(message)
                // 400 / 403 / 409 (REVIEW_NOT_REQUIRED, NOT_ALLOWED, INSPECTION_OPEN, APPROVAL_STARTED): the state
                // changed elsewhere - show the current buttons
                if ([400, 403, 409].includes(error?.status)) ctx.reload()
                return false
            })
            .finally(() => {
                if (mountedRef.current) setBusy(false)
            })
    }

    const markDone = () => {
        const problem = validateReviewNote(note)
        if (problem) {
            errorToast(problem)
            return Promise.resolve()
        }
        return run(
            () => QuestionThreadService.markReviewDone(ctx.auditId, buildReviewDonePayload({ note })),
            'Review marked as done.',
        ).then(ok => {
            if (ok && mountedRef.current) {
                setDialogOpen(false)
                setNote('')
            }
        })
    }

    const undo = () => {
        setConfirmUndo(false)
        run(() => QuestionThreadService.undoReview(ctx.auditId), 'Review is open again.')
    }

    const stillOpenText = openCommentsWarning(stillOpen, review.ApprovalWaitsForComments)

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Label size="small" color={review.Done ? 'green' : 'grey'} basic>
                {review.Done && <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                {reviewStatusText(review)}
            </Label>
            {review.Done && review.Note && <span style={{ color: '#4b5563' }}>"{review.Note}"</span>}
            {!review.Done && review.CanMarkDone && (
                <Button type="button" size="small" primary disabled={busy} onClick={() => setDialogOpen(true)}>
                    Mark review done
                </Button>
            )}
            {review.Done && review.CanUndo && (
                <Button type="button" size="small" basic loading={busy} disabled={busy} onClick={() => setConfirmUndo(true)}>
                    Undo
                </Button>
            )}
            <Confirm
                open={confirmUndo}
                size="tiny"
                header="Undo review done"
                content="The review is marked as not done again and the approval waits until it is marked done."
                cancelButton="Keep it done"
                confirmButton="Undo"
                onCancel={() => setConfirmUndo(false)}
                onConfirm={undo}
            />
            <Modal open={dialogOpen} size="tiny" onClose={() => !busy && setDialogOpen(false)}>
                <Modal.Header>Mark review done</Modal.Header>
                <Modal.Content>
                    {stillOpenText && <Message warning content={stillOpenText} />}
                    <Form>
                        <TextArea
                            rows={3}
                            value={note}
                            maxLength={MAX_REVIEW_NOTE}
                            placeholder="Note (optional)"
                            disabled={busy}
                            onChange={(_, d) => setNote(d.value || '')}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button type="button" basic disabled={busy} onClick={() => setDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button type="button" primary loading={busy} disabled={busy} onClick={markDone}>
                        Mark review done
                    </Button>
                </Modal.Actions>
            </Modal>
        </span>
    )
}

/** Inspection header: comment counts (Open / Replied / Closed), "Show only questions with comments", review done step. */
export const QuestionThreadsSummary = () => {
    const ctx = useQuestionThreads()
    if (!ctx.available) return null
    const total = ctx.counts.Open + ctx.counts.Replied + ctx.counts.Closed
    return (
        <div
            style={{
                margin: '12px 16px 0',
                padding: '10px 14px',
                background: '#f5f7ff',
                border: '1px solid #c7cdf5',
                borderRadius: 8,
                color: '#1f2937',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
            }}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                <ChatsCircle size={18} /> Review comments
            </span>
            <Label size="small" color="red" basic>
                Open
                <Label.Detail>{ctx.counts.Open}</Label.Detail>
            </Label>
            <Label size="small" color="orange" basic>
                Replied
                <Label.Detail>{ctx.counts.Replied}</Label.Detail>
            </Label>
            <Label size="small" color="green" basic>
                Closed
                <Label.Detail>{ctx.counts.Closed}</Label.Detail>
            </Label>
            <Checkbox
                toggle
                label="Show only questions with comments"
                checked={ctx.filterOn}
                disabled={!total && !ctx.filterOn}
                onChange={(_, d) => ctx.setFilterOn(!!d.checked)}
            />
            {ctx.canComment && !total && (
                <span style={{ color: '#4b5563' }}>Use "Add comment" under a question to ask the department.</span>
            )}
            <ReviewDoneControl />
        </div>
    )
}
